import { Link } from 'react-router-dom';

interface BudgetBarProps {
  weeklyBudget: number | null;
  currentCost: number | null;
  pricedCount: number;
  unpricedCount: number;
  recipeCost?: number | null;
  leftoverValue?: number | null;
  estimatedMinimumWeeklyBudget?: number | null;
  truePurchaseStatus?: 'under' | 'near' | 'over' | 'unknown';
}

type Tone = 'under' | 'near' | 'over';

function toneFor(ratio: number): Tone {
  if (ratio > 1) return 'over';
  if (ratio >= 0.8) return 'near';
  return 'under';
}

const TONE_COLOR: Record<Tone, string> = {
  under: 'var(--accent)',
  near: '#f59e0b',
  over: '#e11d48',
};

export function BudgetBar({
  weeklyBudget,
  currentCost,
  pricedCount,
  unpricedCount,
  recipeCost,
  leftoverValue,
  estimatedMinimumWeeklyBudget,
  truePurchaseStatus,
}: BudgetBarProps) {
  if (currentCost == null) return null;

  if (weeklyBudget == null) {
    return (
      <div
        className="card p-4 mb-6 flex items-center justify-between gap-4"
        style={{ borderColor: 'var(--line-soft)' }}
      >
        <div>
          <div className="text-xs font-semibold tracking-wider uppercase mb-1" style={{ color: 'var(--ink-3)' }}>
            Estimated total
          </div>
          <div className="h-display" style={{ fontSize: 24, lineHeight: 1, color: 'var(--ink)' }}>
            ${currentCost.toFixed(2)}
          </div>
        </div>
        <Link
          to="/profile"
          className="text-sm font-medium hover:underline whitespace-nowrap"
          style={{ color: 'var(--accent)' }}
        >
          Set a budget →
        </Link>
      </div>
    );
  }

  const ratio = weeklyBudget > 0 ? currentCost / weeklyBudget : 0;
  const tone = toneFor(ratio);
  const color = TONE_COLOR[tone];
  const fillPct = Math.min(100, Math.max(0, ratio * 100));
  const overAmount = tone === 'over' ? currentCost - weeklyBudget : 0;

  return (
    <div className="card p-5 mb-6" style={{ borderColor: 'var(--line-soft)' }}>
      <div className="flex items-end justify-between mb-2 gap-4">
        <div>
          <div className="text-xs font-semibold tracking-wider uppercase mb-1" style={{ color: 'var(--ink-3)' }}>
            Estimated
          </div>
          <div className="h-display flex items-baseline gap-2" style={{ fontSize: 24, lineHeight: 1, color: 'var(--ink)' }}>
            ${currentCost.toFixed(2)}
            {tone === 'over' && (
              <span className="text-sm font-medium" style={{ color }}>
                ${overAmount.toFixed(2)} over budget
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-semibold tracking-wider uppercase mb-1" style={{ color: 'var(--ink-3)' }}>
            Budget
          </div>
          <div className="text-sm" style={{ color: 'var(--ink-2)' }}>
            of ${weeklyBudget.toFixed(2)}/wk
          </div>
        </div>
      </div>

      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ background: 'var(--surface-2)' }}
        role="progressbar"
        aria-valuenow={Math.round(fillPct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${fillPct}%`, background: color }}
        />
      </div>

      {recipeCost != null && recipeCost > 0 && recipeCost !== currentCost && (
        <div className="text-xs mt-2" style={{ color: 'var(--ink-3)' }}>
          Recipe quantities only: ${recipeCost.toFixed(2)}
        </div>
      )}

      {leftoverValue != null && leftoverValue > 0 && (
        <div className="text-xs mt-1 font-medium" style={{ color: 'oklch(60% 0.16 142)' }}>
          ~${leftoverValue.toFixed(2)} in groceries carries over to next week
        </div>
      )}

      {estimatedMinimumWeeklyBudget != null &&
        truePurchaseStatus === 'over' &&
        currentCost > weeklyBudget && (
          <div className="mt-2 rounded border bg-red-50 border-red-200 p-2 text-xs dark:bg-red-950/20" style={{ color: 'var(--ink-2)' }}>
            Budget may be too low — estimated minimum: ${estimatedMinimumWeeklyBudget.toFixed(2)}/week
          </div>
        )}

      {unpricedCount > 0 && (
        <div className="text-xs mt-2" style={{ color: 'var(--ink-3)' }}>
          Price TBD for {unpricedCount} {unpricedCount === 1 ? 'item' : 'items'}
          {pricedCount > 0 && ` (${pricedCount} priced)`}
        </div>
      )}
    </div>
  );
}
